import {
  VStack,
  FormControl,
  FormErrorMessage,
  Input,
  CompanyNumberInput,
  AddressInput,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Enterprise } from '../../../../../../../types/ProductFactory';
import { CompanyRegistration } from '../../../../../../parties/src/types';
import { Address } from '../../../../../../../types/Common';
import { string, object } from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import QuestionTitle from '../../../../../../sales-conversation/src/components/legacy/questions/QuestionTitle';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupBody from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupBody';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import QuestionGroupSubViewWrapper from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupSubViewWrapper';

interface Props {
  isMobile?: boolean;
  company: Enterprise;
  previousSubView: () => void;
  nextQuestionHandler: (values: Enterprise) => void;
  handleOnChange: (arg0: Enterprise | boolean) => void;
}

interface FormValues {
  name: string;
  company_registration: CompanyRegistration;
  address: Address;
}

const CompanyRegistrationData: React.FC<React.PropsWithChildren<Props>> = ({
  company,
  previousSubView,
  nextQuestionHandler,
}) => {
  const { t } = useTranslation();

  // Helper function to clean address data - convert null/empty values to undefined
  const cleanAddressData = (address: Address | undefined): Address => {
    if (!address) return {};

    const cleanValue = (value: unknown): string | undefined => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      return String(value);
    };

    return {
      country_code: address.country_code || undefined,
      street: cleanValue(address.street),
      housenr: cleanValue(address.housenr),
      boxnr: cleanValue(address.boxnr),
      zipcode: cleanValue(address.zipcode),
      city: cleanValue(address.city),
    };
  };

  // Company registration validation patterns
  const companyNumberPatterns: Record<string, string> = {
    KVK: '^\\d{8}$', // 8 digits for Dutch KVK
    CBE: '^\\d{4}\\.\\d{3}\\.\\d{3}$', // Belgian CBE with dots
    KVK_AW: '^H\\d{5}\\.\\d$', // Aruba
    SIREN: '^\\d{9}$', // French SIREN
    SIRET: '^\\d{14}$', // French SIRET
    HRN: '^HR\\d{4}$', // German HRN
    KVK_BQ: '^H\\d{5}\\.\\d$', // Bonaire
    KVK_SX: '^H\\d{5}\\.\\d$', // Sint Maarten
    KVK_CW: '^H\\d{5}\\.\\d$', // Curaçao
  };

  const companyDataValidationSchema = object().shape({
    name: string().required(t('REQUIRED_FIELD')),
    company_registration: object({
      nr: string()
        .required()
        .test(
          'format',
          t('FLOWS_INPUT_COMPANY_REGISTRATION_INVALID_ERROR'),
          function (value) {
            const { type } = this.parent;
            if (!value || !type) return false;

            const pattern = companyNumberPatterns[type];
            if (!pattern) return true; // If no pattern, allow it

            const regex = new RegExp(pattern);
            return regex.test(value);
          },
        ),
      type: string().required(),
    }).required(t('REQUIRED_FIELD')),
    address: object().nullable(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(companyDataValidationSchema),
    defaultValues: {
      name: company.name || '',
      company_registration: {
        nr: company.company_registration?.nr || company.cbe || '',
        type: company.company_registration?.type || 'CBE',
      },
      address: cleanAddressData(company.address),
    },
  });

  const onSubmit = (data: FormValues) => {
    const updatedCompany: Enterprise = {
      ...company,
      name: data.name,
      cbe: data.company_registration.nr,
      company_registration: data.company_registration,
      address: data.address,
    };
    nextQuestionHandler(updatedCompany);
  };

  const Question = () => (
    <QuestionGroupAssistantQuestion
      question={t('Is this info about your company correct?')}
      description={t('If not, you can edit the details that are incorrect')}
    />
  );

  return (
    <AdvisoryFlowMainContainer data-test-id="ProdFac_CompanyRegistrationData">
      <Question />
      <QuestionGroupBody>
        <QuestionGroupSubViewWrapper>
          <VStack
            spacing="6"
            align="stretch"
            maxW={{ base: '100%', laptop: '60rem' }}
            w="100%"
          >
            {/* Company Name */}
            <div>
              <QuestionTitle>
                {t('FLOWS_INPUT_COMPANY_NAME_LABEL')}
              </QuestionTitle>
              <Controller
                control={control}
                name="name"
                render={({ field: { value, onChange, ref } }) => (
                  <FormControl isInvalid={!!errors.name} ref={ref}>
                    <Input
                      value={value}
                      onChange={onChange}
                      placeholder={t('FLOWS_INPUT_COMPANY_NAME_PLACEHOLDER')}
                    />
                    {errors.name?.message && (
                      <FormErrorMessage>{errors.name.message}</FormErrorMessage>
                    )}
                  </FormControl>
                )}
              />
            </div>

            {/* Company Registration Number */}
            <div>
              <QuestionTitle>
                {t('FLOWS_INPUT_COMPANY_REGISTRATION_NUMBER_LABEL')}
              </QuestionTitle>
              <Controller
                control={control}
                name="company_registration"
                render={({ field: { value, onChange, ref } }) => (
                  <FormControl
                    isInvalid={!!errors.company_registration}
                    ref={ref}
                  >
                    <CompanyNumberInput value={value} onChange={onChange} />
                    {errors.company_registration?.message && (
                      <FormErrorMessage>
                        {errors.company_registration.message}
                      </FormErrorMessage>
                    )}
                    {errors.company_registration?.nr?.message && (
                      <FormErrorMessage>
                        {errors.company_registration.nr.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                )}
              />
            </div>

            {/* Address */}
            <div>
              <QuestionTitle>{t('FLOWS_INPUT_ADDRESS_LABEL')}</QuestionTitle>
              <Controller
                control={control}
                name="address"
                render={({ field: { value, onChange, ref } }) => (
                  <FormControl isInvalid={!!errors.address} ref={ref}>
                    <AddressInput
                      value={value}
                      onChange={onChange}
                      isManualAllowed={true}
                      isDefaultManual={true}
                      error={
                        errors.address?.message && t(errors.address.message)
                      }
                    />
                  </FormControl>
                )}
              />
            </div>
          </VStack>
        </QuestionGroupSubViewWrapper>
      </QuestionGroupBody>
      <QuestionGroupActions
        onBack={previousSubView}
        onContinue={handleSubmit(onSubmit)}
      />
    </AdvisoryFlowMainContainer>
  );
};

export default CompanyRegistrationData;
